import { levelForXp } from './curve';
import { MAX_MASTERY, withTallyEntry } from './mastery';
import { withRecord } from './record';
import { CHANNEL, randBelow, randRange } from './rng';
import {
  SPECIAL_COIN_MULTIPLIER,
  bestUnlockedSpecies,
  speciesById,
  type Species,
} from './species';
import {
  MAX_COINS,
  MAX_COUNT,
  MAX_WEIGHT_G,
  MAX_XP,
  type SimState,
} from './state';
import { OFFLINE_HOLD_TICKS, canAdvanceFrom, tickAtMs } from './time';
import { VARIANT_ROLL_SPACE, variantFor, variantKey, weighVariant } from './variants';

/**
 * THE RATE ENGINE.
 *
 * This module and everything it imports is the code that decides how much a
 * player earns. It must never be able to see what a player has bought.
 * entitlements.test.ts walks the import closure of this file and fails if
 * entitlements.ts ever appears in it, so the firewall is a measured fact about
 * the module graph rather than a promise in a comment.
 *
 * ONE TICK IS THE ONLY UNIT. stepTick is the entire simulation. advance() runs
 * it over an integer tick range and does nothing else, which is why an offline
 * settle and a live run are the same loop over the same indices in the same
 * order — and therefore bit-identical, not approximately equal.
 */

/**
 * Integer addition with a ceiling, so no accumulator can leave safe-integer range.
 *
 * A CEILING IS NOT A NERF: nothing already earned decreases, it only stops
 * rising. The previous body said that in a comment and did the opposite, and
 * SIM-D4 is where it bit. `n > cap ? cap : n` REDUCES a running total that is
 * already above the ceiling, which is the state a save written by a build with a
 * higher cap arrives in - so a 600,000,000-xp save loaded into this build would
 * be pulled to 200,000,000 by the first catch even if the loader had left it
 * alone. The rule is now literal: the result is never smaller than `a`.
 */
function addCapped(a: number, b: number, cap: number): number {
  const n = a + b;
  if (n <= cap) return n;
  return a > cap ? a : cap;
}

/**
 * Ticks this cast takes.
 *
 * DESIGN LAW: skill buys speed and never gates the catch. Skill appears here,
 * in the duration, and NOWHERE in the outcome roll below. At skill 0 the game
 * plays itself and lands every outcome it would otherwise land; at skill 100 it
 * lands them in HALF THE TIME, ROUNDED UP — the halving is `castTicks -
 * floor(castTicks / 2)`, which is ceil(castTicks / 2), so an odd 5-tick cast
 * becomes 3 and never 2. The docstring used to say "floor-rounded", which is the
 * other rounding and would have made a pacing model one tick short on every cast
 * for the 7 of 24 species with an odd duration.
 *
 * SKILL IS A PERSISTED ATTRIBUTE OF THE SAVE, NEVER A LIVE INPUT (owner ruling,
 * 2026-08-31, PRESENCE CHANGES NOTHING). A skilled player is skilled while away,
 * so this shortens a cast identically whether anyone is watching. Nothing in this
 * file can see whether the tab is focused, and there is no argument through which
 * it could be told.
 */
export function castTicksFor(species: Species, skill: number): number {
  const k = Number.isFinite(skill) ? Math.min(100, Math.max(0, Math.trunc(skill))) : 0;
  const d = species.castTicks - Math.floor((species.castTicks * k) / 200);
  return d < 1 ? 1 : d;
}

/**
 * THE QUARRY — what this tick is fishing FOR: the player's choice if it is
 * unlocked, else the best they have.
 *
 * This decides WHICH fish a landing is and how much it weighs, and NOTHING
 * ELSE. It does not decide how long the cast takes, how often it lands junk,
 * how often it lands a special, or what it pays. Those are `pace`, below.
 * See the design law on stepTick.
 */
export function resolveSpecies(state: SimState, level: number): Species {
  if (state.targetSpecies !== '') {
    const chosen = speciesById(state.targetSpecies);
    if (chosen !== undefined && chosen.minLevel <= level) return chosen;
  }
  return bestUnlockedSpecies(level);
}

/**
 * THE TICK. One pure function of (state, tickIndex) and nothing else.
 *
 * `tickIndex` is authoritative — every random draw addresses it directly, so
 * the same index always produces the same outcome no matter how the caller got
 * there. The cursor can never move backwards.
 *
 * ---------------------------------------------------------------------------
 * DESIGN LAW: THE PACE IS THE PLAYER'S, THE QUARRY IS THE PLAYER'S CHOICE.
 * ---------------------------------------------------------------------------
 *
 * CONTRACT.md section 6, THE PLAYER'S ONE VERB: "Presence changes WHAT you
 * catch, never HOW MUCH... Targeting changes composition, not throughput. A
 * targeted run and an untargeted run over the same seed land within the same
 * catch budget; what differs is the distribution across species."
 *
 * ROUND 3 DID THE OPPOSITE, AND IT WAS MEASURED (SIM-F3,
 * `node src/sim/measure/targeting.mjs`, results/r4/targeting-BEFORE.json):
 * `resolveSpecies` swapped the WHOLE species record, so choosing a fish also
 * chose its cast length, its junk odds, its special odds, its xp and its coins.
 * At level 99 over one 24h hold that was 3,414 landings untargeted against
 * 19,456 targeting minnow — a 5.70x throughput swing — and 47.1x on coins. The
 * clause said the choice must not move throughput and the engine made it the
 * only thing the choice moved.
 *
 * The two roles are now separated, and the separation is the whole fix:
 *
 *   PACE   — `bestUnlockedSpecies(level)`. The tempo of the water you are
 *            qualified to fish: cast length, junk odds, special odds, xp and
 *            coins per landing. IT CANNOT SEE `targetSpecies`. That is not a
 *            convention; there is no expression below in which the target
 *            reaches any of those five quantities.
 *   QUARRY — `resolveSpecies(state, level)`. WHICH fish comes up, and therefore
 *            what it weighs and which key it lands on in the catch map.
 *
 * The consequence is exact rather than statistical: two runs over the same seed
 * with different targets produce IDENTICAL landings, xp, coins, specials and
 * junk — the same integers, not merely a similar rate — because nothing on the
 * tempo path reads the choice. Only `catches` and `weightG` differ, and those
 * two ARE the composition. `targeting.test.ts` asserts the equality with zero
 * tolerance across every species and several seeds.
 *
 * WHAT THE CHOICE COSTS, since section 6 property 3 requires that it cost
 * something. THE COST IS PER-SPECIES MASTERY, which is what the owner ruled it
 * to be, and it is a field now rather than a sentence. Every landing credits the
 * cast's own length in ticks to the species it landed, so the hours are spent
 * whatever you choose and the only thing the verb decides is which species they
 * land in. Chasing A for a hundred hours is a hundred hours B did not get. See
 * mastery.ts for the derivation and MASTERY_FULL, the single constant in it that
 * is a guess.
 *
 * WEIGHT IS THE SECOND COST AND IT WAS NEVER SUFFICIENT ALONE. Total weight is
 * the sum over the composition of each fish's mass, so it moves with the choice
 * and cannot be held flat without making every fish weigh the same. But weight
 * ALONE leaves the verb a button on aggregates: with mastery totalled rather
 * than read per species, the heaviest fish beats every other target on every
 * aggregate axis, and `tools/lanes/progression/dominance.mjs --axes aggregate`
 * MEASURES that domination on this engine rather than arguing about it. It is
 * the per-species split that answers property 3, which is exactly what the owner
 * said and is why "mastery" and not "weight" is the word in the ruling.
 *
 * THE FICTION THIS BUYS AND WHAT IT COSTS, stated rather than discovered later:
 * a minnow landed at level 99 pays what the level-99 water pays, which is more
 * than a minnow is "worth". The pay is for the fishing, not for the fish — the
 * hours, the water and the skill are the player's, and the species is the
 * trophy. That is the only reading under which "the same catch budget" and "a
 * distribution that differs" can both be true, and section 6 asks for both.
 */
export function stepTick(state: SimState, tickIndex: number): SimState {
  const t = Number.isFinite(tickIndex) ? Math.trunc(tickIndex) : state.tick;

  // `tickIndex` IS A CHECKED ASSERTION OF THE CURSOR, NOT A SEEK. Any index but
  // the cursor's own is a no-op, in BOTH directions, because both directions are
  // the same hazard:
  //
  //   BACKWARDS — a caller re-running an old index would advance the cast and
  //   award the outcome while the cursor stood still: progress from time already
  //   paid for.
  //   FORWARDS — a caller passing `state.tick + 999` would move the cursor a
  //   thousand ticks while paying exactly one, destroying 999 ticks of earned
  //   progress in a single call, and awarding the outcome belonging to the far
  //   end of the jump rather than the near end.
  //
  // Only the backward case used to be guarded, and only it had a comment. It is
  // symmetric, so the guard is symmetric. advance() always passes the cursor;
  // a replay, a retry, a UI scrubber or a future catch-up helper might not.
  if (t !== state.tick) return state;

  const nextTick = t + 1;

  const level = levelForXp(state.xp);
  // THE PACE. A function of level alone, so the player's choice cannot reach
  // it. Every throughput quantity below is read off this and never off `quarry`.
  const pace = bestUnlockedSpecies(level);
  // THE QUARRY. What comes up, and therefore its weight and its catch key.
  const quarry = resolveSpecies(state, level);
  const duration = castTicksFor(pace, state.skill);
  const progress = state.castProgress + 1;

  if (progress < duration) {
    return { ...state, tick: nextTick, castProgress: progress };
  }

  // The cast resolves. Three outcomes, always three: junk, special, fish.
  const roll = randBelow(state.seed, t, CHANNEL.OUTCOME, 1000);

  if (roll < pace.junkPer1000) {
    return {
      ...state,
      tick: nextTick,
      castProgress: 0,
      junk: addCapped(state.junk, 1, MAX_COUNT),
    };
  }

  if (roll < pace.junkPer1000 + pace.specialPer1000) {
    return {
      ...state,
      tick: nextTick,
      castProgress: 0,
      specials: addCapped(state.specials, 1, MAX_COUNT),
      xp: addCapped(state.xp, pace.xp, MAX_XP),
      coins: addCapped(state.coins, pace.coins * SPECIAL_COIN_MULTIPLIER, MAX_COINS),
    };
  }

  // WEIGHT IS DRAWN FOR THE QUARRY, not the pace: a cod weighs what a cod
  // weighs. Weight is a COMPOSITION axis, not a throughput one, which is why the
  // choice is allowed to move it at all.
  const drawn = randRange(
    state.seed,
    t,
    CHANNEL.WEIGHT,
    quarry.minWeightG,
    quarry.maxWeightG,
  );

  // THE FORM. CONTRACT.md section 1.5l(a): "Mastering mackerel changes mackerel:
  // larger and rarer variants begin appearing... and forms exist that a
  // low-mastery player never sees." This is the whole of that clause on the rate
  // engine, and it is four lines because the properties leave it nowhere else to
  // go. See variants.ts for the derivation and for which figures are guesses.
  //
  // FOUR THINGS ABOUT THIS, each load-bearing:
  //
  //   IT READS `state.mastery[quarry.id]` AND NOTHING ELSE ABOUT MASTERY. The
  //   form of a mackerel is decided by mackerel hours. It is the mastery held
  //   BEFORE this landing credits, which makes the threshold cross on the cast
  //   AFTER the one that reached it — deterministic, identical live and settled,
  //   and one tick is not a quantity anything reports.
  //
  //   IT IS THE COMPOSITION PATH, NOT THE TEMPO PATH, and that distinction is
  //   what keeps property 2 exact. `duration`, the outcome roll, `pace.xp` and
  //   `pace.coins` are all decided above this line and none of them can see
  //   mastery. Two players fishing the same water land the same number of fish
  //   for the same coins; only the fish differ. `state.mastery` now appears in
  //   this file TWICE — here on the right of a read, and below on the right of
  //   the assignment that writes it — where it used to appear once, and
  //   mastery.ts and state.ts both say so rather than carrying the old sentence.
  //
  //   THE ROLL HAS ITS OWN CHANNEL. Sharing CHANNEL.WEIGHT would correlate "is
  //   this an Elder" with "is this a big one" on every tick, and the record a
  //   rare form produced would be a property of the hash rather than of the fish.
  //
  //   THE PLAIN FISH IS UNCHANGED, BIT FOR BIT. `variantFor` returns null below
  //   the first threshold and `weighVariant` returns its argument for null, so a
  //   player who has never mastered anything gets exactly the engine that
  //   shipped last round — same weights, same records, same totals.
  const variant = variantFor(
    state.mastery[quarry.id] ?? 0,
    randBelow(state.seed, t, CHANNEL.VARIANT, VARIANT_ROLL_SPACE),
  );
  const weight = weighVariant(drawn, variant);
  const landed = addCapped(state.catches[quarry.id] ?? 0, 1, MAX_COUNT);

  // MASTERY IS CREDITED HERE, AND THIS IS WHERE SECTION 6 PROPERTY 3'S COST
  // LANDS. `duration` is the cast's own length in ticks, so one point is one
  // second of fishing and a total divided by 3,600 is hours.
  //
  // THREE THINGS ABOUT THIS EXPRESSION, each load-bearing:
  //
  //   IT IS KEYED ON `quarry.id`, THE SAME EXPRESSION THE CATCH MAP USES. Owner
  //   ruling 1.5j(d): bycatch counts toward the species LANDED, so a bycaught
  //   mackerel ticks mackerel mastery whatever you were chasing. Bycatch is not
  //   built yet; this line needs no edit when it is, because it already credits
  //   whatever came up rather than whatever was asked for.
  //
  //   IT USES `duration`, NOT 1. A landing counts for the time it took, which
  //   makes mastery invariant under skill: skill 100 halves the cast and doubles
  //   the landings, and the product is the hour. Crediting 1 per landing would
  //   have made `skill` a mastery rate lever, and skill is the one field in this
  //   state a monetisation layer would have to reach to sell speed.
  //
  //   NO TEMPO READS IT, AND THAT HALF IS STILL FORCED. Property 2 requires a
  //   targeted and an untargeted run to land the same catch budget with ZERO
  //   tolerance; mastery only accumulates under a target, so any rate that read
  //   it would break that equality on the first tick it applied. `duration`, the
  //   outcome roll, `pace.xp` and `pace.coins` therefore cannot see it and there
  //   is no expression above in which they could.
  //
  //   THE COMPOSITION PATH DOES READ IT NOW, once, above, to decide which forms
  //   of the quarry are available (CONTRACT.md section 1.5l(a); see
  //   variants.ts). This comment used to say mastery appeared in this file
  //   exactly once and that nothing read it at all. That was true for one round
  //   and is not true now, and a stale sentence about a firewall is worse than
  //   no sentence: the honest statement is that mastery moves WHAT you land and
  //   never HOW FAST, which is the same line property 2 draws for the target.
  const mastered = addCapped(state.mastery[quarry.id] ?? 0, duration, MAX_MASTERY);

  return {
    ...state,
    tick: nextTick,
    castProgress: 0,
    xp: addCapped(state.xp, pace.xp, MAX_XP),
    coins: addCapped(state.coins, pace.coins, MAX_COINS),
    weightG: addCapped(state.weightG, weight, MAX_WEIGHT_G),
    catches: withTallyEntry(state.catches, quarry.id, landed),
    mastery: withTallyEntry(state.mastery, quarry.id, mastered),
    // THE PERSONAL BEST, AND IT IS THE ONE EARNED FIELD HERE THAT DOES NOT ADD.
    //
    // `withRecord` keeps the LARGER of the standing record and this cast's draw,
    // which is why it takes `weight` rather than a running total. Every other
    // line above rises by construction because addition rises; this one rises
    // only because a comparison says so, and the common case is the smaller
    // value — a minnow after a cod. Writing `weight` here instead of the maximum
    // would nerf a record several times a minute and leave every other fence in
    // this lane green, because `catches`, `mastery` and `weightG` would all
    // still be exactly right. record.test.ts is the fence that is not.
    //
    // IT IS KEYED ON `quarry.id`, the same expression the catch map and the
    // mastery map use, so owner ruling 1.5j(d) holds here without a second
    // decision: a bycaught mackerel sets a mackerel record. Bycatch is not built
    // yet and this line needs no edit when it is.
    //
    // NOTHING BELOW OR ABOVE READS IT, which is forced by property 2 exactly as
    // it is for mastery: a record accumulates only under a target, so any rate
    // that read it would break the targeted/untargeted equality on the first
    // tick it applied.
    records: withRecord(state.records, quarry.id, weight),
    // THE FORM TALLY, AND IT IS THE ONE LINE HERE THAT USUALLY DOES NOTHING.
    //
    // Most landings are plain fish, so `variant` is null and the map is returned
    // UNCHANGED — the same object, not an equal copy, exactly as `withRecord`
    // does for a weight that misses the standing record. `roundtrip.test.ts`
    // proves (load . save) is a fixed point on RAW bytes, and a line that
    // rebuilt this map 950 times in every thousand landings would be one
    // careless edit away from ordering its keys by when a form was last seen,
    // which is SIM-D2 exactly.
    //
    // IT IS KEYED ON `quarry.id`, the same expression the catch map, the mastery
    // map and the record map all use, so owner ruling 1.5j(d) holds here without
    // a second decision: a bycaught mackerel tallies a mackerel form. Bycatch is
    // not built yet and this line needs no edit when it is.
    variants:
      variant === null
        ? state.variants
        : withTallyEntry(
            state.variants,
            variantKey(quarry.id, variant.id),
            addCapped(state.variants[variantKey(quarry.id, variant.id)] ?? 0, 1, MAX_COUNT),
          ),
  };
}

export interface AdvanceReport {
  readonly state: SimState;
  /** Ticks actually simulated. Never more than OFFLINE_HOLD_TICKS in one call. */
  readonly paidTicks: number;
  /** Ticks that fell beyond the hold and were skipped outright. Never paid, ever, anywhere. */
  readonly skippedTicks: number;
}

/**
 * THE HOLD, SPLIT ONCE.
 *
 * This is the ONLY place in the codebase where OFFLINE_HOLD_TICKS is compared
 * against anything. Every caller that needs to know what an absence pays asks
 * this function; nobody re-derives it. The named trap for this design is a hold
 * enforced in two places, where the second place quietly pays out the remainder
 * the first place dropped — hold.test.ts greps the source tree and fails if a
 * second reference to the constant appears outside time.ts.
 *
 * Skipped time is SKIPPED. It is not paid at a reduced rate, because there is no
 * reduced rate anywhere in this file for it to be paid at.
 *
 * WHICH END IS PAID, and why. advance() pays the FIRST OFFLINE_HOLD_TICKS of an
 * absence and drops the tail. Paying the tail instead would mean simulating from
 * a state no live player ever occupied, and the offline claim would stop being a
 * literal equality — the whole point is that the paid ticks are the SAME tick
 * indices, in the SAME order, that a present player would have run.
 */
export function splitAbsence(gap: number): { readonly paid: number; readonly skipped: number } {
  if (!(gap > 0)) return { paid: 0, skipped: 0 };
  const paid = gap > OFFLINE_HOLD_TICKS ? OFFLINE_HOLD_TICKS : gap;
  return { paid, skipped: gap - paid };
}

/**
 * ADVANCE — the production caller. The app calls this and nothing else to move
 * time forward, whether it is a focused tab at 60fps, a throttled background tab
 * at one callback a minute, or a tab reopened after two days.
 *
 * The target tick is computed FROM THE TIMESTAMP, never from accumulated ticks
 * or callback counts, so cadence is irrelevant to the result. offline.test.ts
 * proves this by driving the same span at four different cadences through this
 * exact function and asserting deep equality with no tolerance.
 *
 * There is no remainder, no pending balance and no carry: whatever is skipped is
 * gone at the moment it is skipped, so there is nothing a later animation frame
 * could pay out.
 */
export function advanceWithReport(state: SimState, nowMs: number): AdvanceReport {
  const target = tickAtMs(state.epochMs, nowMs);

  // LIVENESS, THE SECOND LOCK — and it tests THE ceiling, not A ceiling.
  //
  // A target at CURSOR_CEILING means some module in this lane is about to
  // rewrite the cursor rather than let it stand. Two ways that happens and both
  // are here in one comparison, because the constant is the minimum of both:
  //   - MAX_TICK: tickAtMs has SATURATED, the anchor is corrupt, and writing
  //     the saturation point into the cursor makes every later gap zero
  //     forever, with no error raised (SIM-C1).
  //   - MAX_SAVE_TICK: the LOADER clamps here, so a cursor written above it is
  //     silently rewound on the next save while its anchor is left untouched —
  //     and the same gap is then paid again on every reload, without bound
  //     (SIM-D1, MEASURED at 233,587 -> 2,512,448 xp over eight frozen-clock
  //     reloads before this line named the right number).
  //
  // AND IT ASKS THE DOOR'S OWN QUESTION, through the door's own function. The
  // two locks used to be two comparisons against two different constants, which
  // is the whole of SIM-D1: they could disagree, and they did. canAdvanceFrom
  // lives in time.ts precisely so both can ask it, because step.ts may never
  // import save.ts - the rate engine cannot be allowed to see the save surface
  // (entitlements.test.ts walks the import closure and fails if it does).
  //
  // Asked of the TARGET rather than the cursor, so what advance is about to
  // WRITE is what gets tested. A cursor the door would refuse to keep is a
  // cursor advance must not write; that is the whole of the invariant, and it
  // is now one predicate instead of two numbers that have to be kept level.
  if (!canAdvanceFrom(state.epochMs, target)) {
    return { state, paidTicks: 0, skippedTicks: 0 };
  }

  const gap = target - state.tick;

  // Covers three cases at once: nothing to do, a NaN target, and a cursor from
  // the future (a save written under a clock that was ahead). Time never runs
  // backwards here, and no earned quantity is ever taken back.
  if (!(gap > 0)) return { state, paidTicks: 0, skippedTicks: 0 };

  const { paid, skipped } = splitAbsence(gap);

  let s = state;
  const until = state.tick + paid;
  for (let t = state.tick; t < until; t++) {
    s = stepTick(s, t);
  }

  if (skipped > 0) {
    // The cursor jumps past the skipped span in the same breath that skips it.
    // Recording the skip and moving the cursor are one operation precisely so
    // that no state can exist in which a skip is owed but not yet taken.
    s = { ...s, tick: target, skippedTicks: addCapped(s.skippedTicks, skipped, MAX_COUNT) };
  }

  return { state: s, paidTicks: paid, skippedTicks: skipped };
}

/** The same call, for callers that do not need the report. */
export function advance(state: SimState, nowMs: number): SimState {
  return advanceWithReport(state, nowMs).state;
}

/**
 * What an absence WOULD pay, without paying it. Pure preview for a returning
 * player's summary screen; changes nothing, and re-derives nothing.
 */
export function previewAbsence(
  state: SimState,
  nowMs: number,
): { readonly paidTicks: number; readonly skippedTicks: number } {
  const { paid, skipped } = splitAbsence(tickAtMs(state.epochMs, nowMs) - state.tick);
  return { paidTicks: paid, skippedTicks: skipped };
}
